import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Target, TrendingUp, Calendar, Globe, Flag, BarChart3 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  under_review: 'Under Review',
  advancing: 'Advancing',
  monitoring: 'Monitoring',
  pursue: 'Pursue',
  request_diligence: 'Request Diligence',
  deprioritised: 'Deprioritised',
};

const STATUS_COLORS = {
  under_review: 'bg-slate-100 text-slate-700 border-slate-200',
  advancing: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  monitoring: 'bg-sky-100 text-sky-700 border-sky-200',
  pursue: 'bg-violet-100 text-violet-700 border-violet-200',
  request_diligence: 'bg-amber-100 text-amber-700 border-amber-200',
  deprioritised: 'bg-rose-100 text-rose-700 border-rose-200',
};

export default function PublicEngagementPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/engagements/public/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || 'Link not found or expired'));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <p className="text-slate-700 font-semibold">{error}</p>
          <p className="text-xs text-slate-500 mt-1">Please contact your Marksman point of contact.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const e = data.engagement;
  const t = data.totals;
  const sb = data.status_breakdown || {};
  const ta = data.therapeutic_area_distribution || [];
  const ph = data.phase_distribution || [];
  const ss = data.scoring_summary || {};

  const maxTaCount = Math.max(1, ...ta.map(x => x.count));
  const maxPhCount = Math.max(1, ...ph.map(x => x.count));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header band */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
            <span className="uppercase tracking-wider">Asset Intelligence Programme</span>
            <span>·</span>
            <span>Prepared by Marksman Healthcare</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="public-engagement-title">
            {e.name}
          </h1>
          <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
            <Badge variant="secondary" className="bg-slate-100"><Globe className="w-3 h-3 mr-1" />{e.country_focus}</Badge>
            <Badge variant="secondary" className="bg-slate-100"><Calendar className="w-3 h-3 mr-1 capitalize" />{e.cadence} cadence</Badge>
            <Badge variant="secondary" className="bg-slate-100"><Target className="w-3 h-3 mr-1" />Cohort size {e.cohort_size}</Badge>
          </div>
          <p className="text-[11px] text-slate-400 mt-3">
            View as of {new Date(data.as_of).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Top metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-5 border-slate-200" data-testid="metric-pool">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Candidate Pool</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{t.candidates_in_pool}</p>
            <p className="text-[10px] text-slate-500 mt-1">total assets identified</p>
          </Card>
          <Card className="p-5 border-slate-200" data-testid="metric-active">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Active Cohort</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{t.active_cohort_size}</p>
            <p className="text-[10px] text-slate-500 mt-1">currently under evaluation</p>
          </Card>
          <Card className="p-5 border-slate-200" data-testid="metric-scored">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Assets Scored</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{ss.assets_scored || 0}</p>
            <p className="text-[10px] text-slate-500 mt-1">multi-criteria AI scored</p>
          </Card>
          <Card className="p-5 border-emerald-200 bg-emerald-50/40" data-testid="metric-high-priority">
            <p className="text-[10px] text-emerald-700 uppercase tracking-wider font-medium">High Priority</p>
            <p className="text-3xl font-bold text-emerald-900 mt-1">{ss.high_priority_count || 0}</p>
            <p className="text-[10px] text-emerald-700 mt-1">composite score ≥ 25</p>
          </Card>
        </div>

        {/* Status breakdown */}
        <Card className="p-6 border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <Flag className="w-4 h-4 text-slate-600" />
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Status Distribution (Active Cohort)</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="status-distribution">
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <div key={k} className={`rounded-lg border px-3 py-3 ${STATUS_COLORS[k]}`}>
                <p className="text-[10px] font-medium uppercase tracking-wider opacity-80">{v}</p>
                <p className="text-2xl font-bold mt-1">{sb[k] || 0}</p>
              </div>
            ))}
          </div>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* TA distribution */}
          <Card className="p-6 border-slate-200" data-testid="ta-distribution">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4 text-slate-600" />
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Therapeutic Area Mix</h2>
            </div>
            <div className="space-y-2">
              {ta.length === 0 && <p className="text-xs text-slate-400">No TA data yet.</p>}
              {ta.map((row, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-700 font-medium truncate flex-1 mr-2">{row.therapeutic_area}</span>
                    <span className="text-slate-500 font-mono">{row.count}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                    <div className="h-full bg-violet-500 rounded-full transition-all" style={{ width: `${(row.count / maxTaCount) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Phase distribution */}
          <Card className="p-6 border-slate-200" data-testid="phase-distribution">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-slate-600" />
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Development Stage</h2>
            </div>
            <div className="space-y-2">
              {ph.length === 0 && <p className="text-xs text-slate-400">No stage data yet.</p>}
              {ph.map((row, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-700 font-medium truncate flex-1 mr-2">{row.phase}</span>
                    <span className="text-slate-500 font-mono">{row.count}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                    <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${(row.count / maxPhCount) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* BD profile / scope card */}
        <Card className="p-6 border-slate-200">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Programme Scope &amp; Priorities</h2>
          {e.bd_profile?.strategic_lens && (
            <p className="text-sm text-slate-700 leading-relaxed mb-4 italic">&ldquo;{e.bd_profile.strategic_lens}&rdquo;</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-xs">
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium mb-2">Therapeutic Areas in Focus</p>
              <div className="flex flex-wrap gap-1.5">
                {(e.bd_profile?.therapeutic_areas || []).map((t, i) => (
                  <Badge key={i} variant="secondary" className="bg-violet-50 text-violet-700 border border-violet-200">{t}</Badge>
                ))}
                {!(e.bd_profile?.therapeutic_areas || []).length && <span className="text-slate-400">—</span>}
              </div>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium mb-2">Stage Preference</p>
              <div className="flex flex-wrap gap-1.5">
                {(e.bd_profile?.stage_preference || []).map((s, i) => (
                  <Badge key={i} variant="secondary" className="bg-emerald-50 text-emerald-700 border border-emerald-200">{s}</Badge>
                ))}
                {!(e.bd_profile?.stage_preference || []).length && <span className="text-slate-400">—</span>}
              </div>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium mb-2">Preferred Modalities</p>
              <div className="flex flex-wrap gap-1.5">
                {(e.bd_profile?.modality_preference || []).map((m, i) => (
                  <Badge key={i} variant="secondary" className="bg-sky-50 text-sky-700 border border-sky-200">{m}</Badge>
                ))}
                {!(e.bd_profile?.modality_preference || []).length && <span className="text-slate-400">—</span>}
              </div>
            </div>
          </div>
        </Card>

        <p className="text-[11px] text-slate-400 text-center">
          Confidential — for {e.client_company} internal use only. Underlying asset and biotech identities are maintained securely by Marksman Healthcare and not disclosed in this view.
        </p>
      </div>
    </div>
  );
}
